import { initReactI18next } from 'react-i18next'
import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import ChainedBackend from 'i18next-chained-backend'
import LocalStorageBackend from 'i18next-localstorage-backend'
import Backend from 'i18next-locize-backend'

import { buildLocizeBackend, parseLocizeCacheMaxAge } from '@/common/utils/locizeCacheConfig'

// Locize translation caching (QM-3418). Locize bills per private download, so we
// chain a localStorage cache in front of the Locize backend: translations are
// served from localStorage and only re-downloaded on a cache miss/expiry.
// Enabled per-env via VITE_LOCIZE_CACHE_MAX_AGE_MS (ms). Production sets ~24h;
// dev/staging leave it at 0 so translators/devs see instant updates.
// cacheHitMode stays at its default ('none') so a cache hit never triggers a
// background Locize re-fetch.
// https://www.locize.com/docs/caching/alternative-caching-with-i18next
const cacheMaxAgeMs = parseLocizeCacheMaxAge(import.meta.env.VITE_LOCIZE_CACHE_MAX_AGE_MS)

const locizeBackendOptions = {
    projectId: import.meta.env.VITE_LOCIZE_PROJECT_ID,
    apiKey: import.meta.env.VITE_LOCIZE_API_KEY,
    private: true,
    referenceLng: import.meta.env.VITE_LOCIZE_REFERENCE_LNG,
}

const { backend, plugin: backendPlugin } = buildLocizeBackend({
    cacheMaxAgeMs,
    locizeOptions: locizeBackendOptions,
    locizeBackend: Backend,
    localStorageBackend: LocalStorageBackend,
    chainedBackend: ChainedBackend,
})

i18n
    // i18next-locize-backend loads translations from your project and saves new
    // keys to it (saveMissing: true). When caching is enabled it is wrapped by
    // i18next-chained-backend (localStorage first) — see buildLocizeBackend.
    // https://github.com/locize/i18next-locize-backend
    .use(backendPlugin)
    // detect user language
    // learn more: https://github.com/i18next/i18next-browser-languageDetector
    .use(LanguageDetector)
    // pass the i18n instance to react-i18next.
    .use(initReactI18next)
    // init i18next
    // for all options read: https://www.i18next.com/overview/configuration-options
    .init({
        detection: {
            order: ['localStorage', 'navigator'],
            lookupLocalStorage: 'webstore-language',
            caches: [],
        },
        fallbackLng: 'en-GB',
        load: 'currentOnly',
        saveMissing: true,
        interpolation: {
            escapeValue: false, // not needed for react as it escapes by default
            prefix: '{{',
            suffix: '}}',
        },
        keySeparator: false,
        nsSeparator: '::',
        backend,
    })

export default i18n
